import { type FC, memo, useMemo, useState } from "react";
import OrderTotals from "~/features/cart/cart/components/OrderTotals";
import Address from "~/features/cart/checkout/components/Address";
import { DateFormatType, formatDate, formatNullableDate } from "~/common/utils/DateFormatter";
import { Col, Row } from "~/common/components/Layout";
import Parameter from "~/common/components/Parameter";
import { Permission, userHasPermission } from "~/domains/user/utils/permissions";
import { sum } from "~/utils/PriceUtils";
import { getOrderLineItemSurchargeValue } from "~/features/orders/common/utils/OrderLineItemUtils";
import { type DetailedOrderData } from "~/domains/orders/types/modelsV2";
import { useTranslation } from "react-i18next";
import { DownloadContentButton } from "~/features/orders/quoteList/components/QuoteListTable/DownloadContentButton";
import type { InvoiceInfoData } from "~/domains/orders/types/models";
import { downloadFile } from "~/common/utils/ApiHelper";

interface OrderSummaryProps {
  data: DetailedOrderData;
}

const OrderDetailsSummary: FC<OrderSummaryProps> = ({ data }) => {
  const canSeeComments = userHasPermission(Permission.ADD_VIEW_HEADER_LEVEL_COMMENTS);
  const totalSurcharge = useMemo(() => {
    return sum(...data.products.map((lineItem) => getOrderLineItemSurchargeValue(lineItem)));
  }, [data]);

  return (
    <>
      {data.comment && canSeeComments && (
        <>
          <Row className={"scha__order-summary mb-3"}>
            <OrderParameters data={data} />
          </Row>

          <Row className={"scha__order-summary"}>
            <Col size={8}>
              <OrderComment data={data} />
            </Col>

            <Col size={4} className="scha__order-summary__totals">
              <OrderTotals showPriceOnRequest={false} weight={data.totalWeight} totalValue={data.totalPrice} />
            </Col>
          </Row>
        </>
      )}

      {(!data.comment || !canSeeComments) && (
        <>
          <Row className={"scha__order-summary"}>
            <OrderParameters data={data} />
          </Row>
          <Row className={"scha__order-summary"}>
            <Col size={8} />
            <Col size={4} className="scha__order-summary__totals">
              <OrderTotals
                showPriceOnRequest={false}
                weight={data.totalWeight}
                totalValue={data.totalPrice}
                surcharge={totalSurcharge.value > 0 ? totalSurcharge : undefined}
              />
            </Col>
          </Row>
        </>
      )}
    </>
  );
};

export default memo(OrderDetailsSummary);

const OrderParameters: FC<OrderSummaryProps> = ({ data }) => {
  const { t } = useTranslation();
  const estimatedDate = orderDetailsDeliveryOptionEstimatedDate(data);
  const [loading, setLoading] = useState({ invoice: false, deliveryNote: false, orderConfirmation: false });

  const handleDocumentDownload = async (type: "invoice" | "deliveryNote" | "orderConfirmation") => {
    if (data.products.length > 0) {
      let url: keyof InvoiceInfoData, number: keyof InvoiceInfoData;
      let documentType: string;
      setLoading({ ...loading, [type]: true });

      if (type === "invoice") {
        url = "invoiceUrl";
        number = "invoiceNo";
        documentType = "INVOICE";
      } else if (type === "deliveryNote") {
        url = "deliveryNoteUrl";
        number = "deliveryNoteNumber";
        documentType = "DELNOTE";
      }

      if (type === "orderConfirmation") {
        documentType = "ORDCONF";
        await downloadFile(`${data.orderCnfDocumentUrl}?id=${data.soNumber}&type=${documentType}`);
      } else {
        const seenFilesDocNo = new Set<string>();
        const downloadPromises = data.products.flatMap((item) => {
          const promises: Promise<unknown>[] = [];

          function handleUniqueDownload(documentUrl: string, documentNumber: string) {
            if (documentUrl && documentNumber && !seenFilesDocNo.has(documentNumber)) {
              seenFilesDocNo.add(documentNumber);
              promises.push(downloadFile(`${documentUrl}?id=${documentNumber}&type=${documentType}`));
            }
          }
          if (item.invoiceData) {
            const documentUrl = item.invoiceData?.[url] as string;
            const documentNumber = item.invoiceData?.[number] as string;
            handleUniqueDownload(documentUrl, documentNumber);
          }
          if (Array.isArray(item.subEntries)) {
            item.subEntries.forEach((subItem) => {
              if (subItem.invoiceData) {
                const documentUrl = subItem.invoiceData?.[url] as string;
                const documentNumber = subItem.invoiceData?.[number] as string;
                handleUniqueDownload(documentUrl, documentNumber);
              }
            });
          }
          return promises;
        });

        await Promise.all(downloadPromises);
      }
      setLoading({ ...loading, [type]: false });
    }
  };

  const haveInvoice =
    data.products.filter(
      (item) => item.invoiceData?.invoiceUrl || item.subEntries.filter((i) => i.invoiceData?.invoiceUrl),
    ).length > 0;
  const haveDeliveryNote =
    data.products.filter(
      (item) => item.invoiceData?.deliveryNoteUrl || item.subEntries.filter((i) => i.invoiceData?.deliveryNoteUrl),
    ).length > 0;
  const haveOrderCnfDocument = (data.orderCnfDocumentUrl?.length ?? 0) > 0;

  return (
    <>
      <Col size={2} className={"scha__order-summary__param-column"}>
        <Parameter data-test-id={"order-status"} label={t("orderDetailsPage.summary.status")}>
          {t(data.orderStatus)}
        </Parameter>
        <Parameter data-test-id={"po-number"} label={t("searchByInput.queryType.po.orderlist")}>
          {data.poNumber}
        </Parameter>
        <Parameter data-test-id={"account-number"} label={t("quoteDetailsPage.summary.accNo")}>
          {data.accNo}
        </Parameter>
      </Col>

      <Col size={2} className={"scha__order-summary__param-column"}>
        <Parameter data-test-id={"so-number"} label={t("orderListPage.table.header.soNumber")}>
          {data.soNumber}
        </Parameter>
        <Parameter data-test-id={"date-placed"} label={t("contractDetailsPage.summary.datePlaced")}>
          {formatNullableDate(data.datePlaced, DateFormatType.DATE)}
        </Parameter>
        <Parameter data-test-id={"order-type"} label={t("orderDetailsPage.summary.orderType")}>
          {t(data.orderType)}
        </Parameter>
      </Col>

      <Col size={2}>
        <Parameter
          data-test-id={"shipment-method"}
          className="mb-2"
          label={t("orderDetailsPage.summary.shipmentMethod")}
        >
          {t(data.shipmentMethod)}
        </Parameter>
        <Parameter data-test-id={"freight-payment"} className="mb-2" label={t("contractDetailsPage.summary.freight")}>
          {t(data.freightPayment)}
        </Parameter>
        {userHasPermission(Permission.COMPLETE_DELIVERY) && (
          <Parameter data-test-id={"delivery-options"} label={t("orderDetails.deliveryOptions.title")}>
            {t(
              data.completeDelivery
                ? "checkoutPage.headers.deliveryOptions.completeDelivery"
                : "checkoutPage.headers.deliveryOptions.partialDevivery",
            )}
            <br />
            {data.completeDelivery &&
              estimatedDate &&
              t("checkoutPage.headers.deliveryOptions.estimateDate", {
                date: formatDate(estimatedDate, DateFormatType.DATE),
              })}
          </Parameter>
        )}
      </Col>

      {data.shippingAddress && (
        <>
          {data.unloadingPoint && (
            <>
              <Col size={2} className={"scha__order-summary__param-column"}>
                <Parameter data-test-id={"shipping-address"} label={t("checkoutPage.headers.shippingAddress")}>
                  <Address as={"span"} data={data.shippingAddress} />
                </Parameter>
                <Parameter data-test-id={"unloading-point"} label={t("checkoutPage.headers.unloading")}>
                  {data.unloadingPoint}
                </Parameter>
              </Col>
              <Col size={2} className={"scha__order-summary__param-column"}>
                <Parameter data-test-id={"sold-to"} label={t("orderDetailsPage.summary.soldToAddress")}>
                  <Address as={"span"} data={data.soldTo} />
                </Parameter>
              </Col>
            </>
          )}
          {!data.unloadingPoint && (
            <>
              <Col size={2} className={"scha__order-summary__param-column"}>
                <Parameter data-test-id={"shipping-address"} label={t("checkoutPage.headers.shippingAddress")}>
                  <Address as={"span"} data={data.shippingAddress} />
                </Parameter>
              </Col>
              <Col size={2} className={"scha__order-summary__param-column"}>
                <Parameter data-test-id={"sold-to"} label={t("orderDetailsPage.summary.soldToAddress")}>
                  <Address as={"span"} data={data.soldTo} />
                </Parameter>
              </Col>
            </>
          )}
        </>
      )}
      {["H3", "H4", "H5", "H6"].includes(data.orderStatusCode) &&
        (haveInvoice || haveDeliveryNote || haveOrderCnfDocument) && (
          <Col size={2}>
            <Parameter data-test-id={"freight-payment"} label={t("orderDetailsPage.summary.download.title")} />
            {["H5", "H6"].includes(data.orderStatusCode) && (
              <>
                {haveInvoice && (
                  <DownloadContentButton
                    className="mb-2"
                    label={t("orderDetailsPage.summary.download.invoices")}
                    onClick={() => handleDocumentDownload("invoice")}
                    isLoading={loading.invoice}
                  />
                )}
                {haveDeliveryNote && (
                  <DownloadContentButton
                    className="mb-2 mt-1"
                    label={t("orderDetailsPage.summary.download.deliveryNotes")}
                    onClick={() => handleDocumentDownload("deliveryNote")}
                    isLoading={loading.deliveryNote}
                  />
                )}
              </>
            )}
            {haveOrderCnfDocument && (
              <DownloadContentButton
                label={t("orderDetailsPage.summary.download.orderConfirmation")}
                onClick={() => handleDocumentDownload("orderConfirmation")}
                isLoading={loading.orderConfirmation}
              />
            )}
          </Col>
        )}
    </>
  );
};

const OrderComment: FC<OrderSummaryProps> = ({ data }) => {
  const { t } = useTranslation();
  return (
    <>
      <Col size={8} className={"scha__order-summary__param-column scha__order-summary__comment pl-0"}>
        <Parameter data-test-id={"comment"} label={t("order.information.header.comment")}>
          {data.comment}
        </Parameter>
      </Col>
    </>
  );
};

export const orderDetailsDeliveryOptionEstimatedDate = (data: DetailedOrderData) => {
  if (!data || data.products.length === 0) {
    return undefined;
  }

  const eddProductList = data.products
    .filter((product) => product.deliveryDate !== undefined && product.deliveryDate !== null)
    .map((product) => product.deliveryDate);

  if (eddProductList.length > 0) {
    eddProductList.sort((a, b) => (a && b ? b?.getTime() - a?.getTime() : 0));
    return eddProductList[0];
  }
  return undefined;
};
